import { useCallback, useState } from 'react';
import type { LearningLesson, LearningSection, LessonPage } from '../services/learningTypes';
import type { AdminLearningService } from '../services/learningService';
import { parseLessonTextFormat } from '../services/textFormatParser';

interface PastedLessonData {
    section: LearningSection;
    lessonData: Partial<LearningLesson>;
    pages: LessonPage[];
}

interface UseLessonClipboardResult {
    copiedLessonId: string | null;
    handlePasteLesson: (section: LearningSection) => Promise<PastedLessonData | null>;
    handlePasteTextFormat: (section: LearningSection) => Promise<PastedLessonData | null>;
    handleCopyLesson: (section: LearningSection, lesson: LearningLesson) => Promise<void>;
}

export const useLessonClipboard = (
    service: AdminLearningService,
    onError: (message: string) => void,
): UseLessonClipboardResult => {
    const [copiedLessonId, setCopiedLessonId] = useState<string | null>(null);

    const handlePasteLesson = useCallback(async (section: LearningSection): Promise<PastedLessonData | null> => {
        try {
            const clipboardText = await navigator.clipboard.readText();
            const lines = clipboardText.trim().split('\n');

            if (lines.length === 0) {
                onError('Clipboard is empty');
                return null;
            }

            const firstLine = lines[0].trim();
            if (!firstLine) {
                onError('First line must contain the lesson title');
                return null;
            }

            const title = firstLine.substring(1).trim();

            const customId = title
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-+|-+$/g, '');

            const remainingContent = lines.slice(1).join('\n').trim();
            const pageSections = remainingContent.split(/^##\s+/gm).filter(s => s.trim());

            if (pageSections.length === 0) {
                onError('No page content found. Each page should start with ##');
                return null;
            }

            const pages: LessonPage[] = pageSections.map((pageContent, index) => {
                const trimmedContent = pageContent.trim();
                const isQuestion = trimmedContent.endsWith('?');

                if (isQuestion) {
                    return {
                        uid: '',
                        content: `## ${trimmedContent}\n\n \`\`\`question\nQuestion\n\`\`\``,
                        buttonText: 'Submit Answer',
                        order: index + 1,
                        type: 'question' as const,
                        shouldSaveAnswer: true,
                        options: [],
                        correctOptionIds: [],
                    };
                }

                return {
                    uid: '',
                    content: `## ${trimmedContent}\n\n \`\`\`learning\nLearning\n\`\`\``,
                    buttonText: 'Continue',
                    order: index + 1,
                    type: 'page' as const,
                };
            });

            const lessonData: Partial<LearningLesson> = {
                uid: customId,
                title,
                description: 'ADD DESCRIPTION HERE',
                isFree: false,
                timeMinutes: Math.max(2, Math.floor(clipboardText.length / 1000)),
                type: 'lesson' as const,
                status: 'draft' as const,
                order: section.lessons.length + 1,
            };

            return { section, lessonData, pages };
        } catch (err) {
            console.error('Error pasting lesson:', err);
            onError(err instanceof Error ? err.message : 'Failed to paste lesson from clipboard');
            return null;
        }
    }, [onError]);

    const handlePasteTextFormat = useCallback(async (section: LearningSection): Promise<PastedLessonData | null> => {
        try {
            const clipboardText = await navigator.clipboard.readText();

            if (!clipboardText.trim()) {
                onError('Clipboard is empty');
                return null;
            }

            const { lessonData, pages } = parseLessonTextFormat(clipboardText, section.lessons.length);
            return { section, lessonData, pages };
        } catch (err) {
            console.error('Error pasting text format lesson:', err);
            onError(err instanceof Error ? err.message : 'Failed to paste lesson from clipboard');
            return null;
        }
    }, [onError]);

    const handleCopyLesson = useCallback(async (section: LearningSection, lesson: LearningLesson) => {
        try {
            const pages = await service.getPagesInLesson(section.uid, lesson.uid);

            let markdown = `# ${lesson.title}\n\n`;
            const sortedPages = [...pages].sort((a, b) => (a.order || 0) - (b.order || 0));

            sortedPages.forEach((page) => {
                const contentWithoutBadges = page.content.replace(/```[\s\S]*?```/g, '').trim();
                const cleanContent = contentWithoutBadges.replace(/^##\s+/, '').trim();
                markdown += `## ${cleanContent}\n\n`;
            });

            await navigator.clipboard.writeText(markdown.trim());

            setCopiedLessonId(lesson.uid);
            setTimeout(() => setCopiedLessonId(null), 2000);
        } catch (err) {
            console.error('Error copying lesson:', err);
            onError(err instanceof Error ? err.message : 'Failed to copy lesson to clipboard');
        }
    }, [service, onError]);

    return { copiedLessonId, handlePasteLesson, handlePasteTextFormat, handleCopyLesson };
};
